/**
 * ClarificationContext — clarification state machine for LAUNCHER-2.0.
 *
 * Handles the lifecycle of agent clarification requests:
 *   idle → pending (CLARIFY event received) → answered (user submits)
 *
 * Registers WS event handler for: CLARIFY.
 *
 * Exposes:
 *   pendingClarification   — null | { queryId, questions, agentId, … }
 *   submitClarification(answers)
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { EventType } from '../services/orchestratorClient';
import { useConnection } from './ConnectionContext';
import { useMessages, generateMsgId } from './MessageContext';
import {
  buildClarificationContext,
  extractDeveloperTrace,
  isClarificationRequest,
  normalizeClarificationQuestions,
} from '../contracts/clarification';

const ClarificationContext = createContext(null);

// ── Answer formatting (pure) ──────────────────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function formatAnswers(answersJson, questions) {
  let parsed;
  try { parsed = JSON.parse(answersJson); } catch { return answersJson; }

  const lines = [];

  const ackQs = questions.filter(q => q.type === 'acknowledge');
  if (ackQs.length > 0 && ackQs.every(q => parsed[q.id] === true)) {
    return '✅ Acknowledged';
  }

  const yesNoQs = questions.filter(q => q.type === 'yes_no');
  const allYes  = yesNoQs.length > 0 && yesNoQs.every(q => parsed[q.id] === true);
  const allNo   = yesNoQs.length > 0 && yesNoQs.every(q => parsed[q.id] === false);

  if (allYes) {
    lines.push('✅ All declarations confirmed');
  } else if (yesNoQs.length > 0) {
    if (allNo) {
      lines.push('❌ All declarations declined');
    } else {
      yesNoQs.forEach(q => {
        const val = parsed[q.id];
        const icon = val ? '✅' : '❌';
        const label = q.id.replace(/^tc_/, '').replace(/_/g, ' ');
        lines.push(`${icon} ${label}`);
      });
    }
  }

  questions.filter(q => q.type === 'select').forEach(q => {
    const val = parsed[q.id];
    if (val !== undefined) lines.push(`📅 Year: ${val}`);
  });

  questions.filter(q => q.type === 'multiselect').forEach(q => {
    const vals = parsed[q.id];
    if (Array.isArray(vals) && vals.length > 0) {
      const names = vals
        .map(v => {
          const str = String(v);
          if (/^\d{4}-\d{1,2}$/.test(str)) {
            const [y, m] = str.split('-').map(Number);
            return m >= 1 && m <= 12 ? `${MONTH_NAMES[m - 1]} ${y}` : str;
          }
          const n = Number(v);
          return n >= 1 && n <= 12 ? MONTH_NAMES[n - 1] : str;
        })
        .join(', ');
      lines.push(`🗓️ Months: ${names}`);
    }
  });

  return lines.length > 0 ? lines.join('\n') : answersJson;
}

export function ClarificationProvider({ children }) {
  const { client } = useConnection();
  const { addMessage, updateMessages, activeConversationRef, lastUserQueryRef } = useMessages();

  const [pendingClarification, setPendingClarification] = useState(null);

  // ── WS handler: CLARIFY ───────────────────────────────────────────────────
  useEffect(() => {
    const handleClarify = (event) => {
      const convId = activeConversationRef.current;
      const questions = normalizeClarificationQuestions(event.clarification_questions || []);
      const trace = extractDeveloperTrace(event);
      const request = {
        needs_clarification: true,
        clarification_questions: questions,
        trace,
      };

      if (!isClarificationRequest(request)) {
        console.warn('[ClarificationContext] Invalid clarification payload:', event);
        addMessage(convId, {
          id: generateMsgId(),
          role: 'system',
          content: '**Error:** The specialist returned an invalid clarification request.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isError: true,
        });
        return;
      }

      updateMessages(convId, (msgs) =>
        msgs.map(m =>
          m.role === 'orchestrator' && m.isStreaming
            ? { ...m, isStreaming: false, content: 'I need a bit more context to give you the best answer:' }
            : m
        )
      );

      setPendingClarification({
        queryId: event.query_id,
        questions,
        agentId: event.agent_id || 'governance-security',
        originalQuery: event.original_query || lastUserQueryRef.current,
        specialistRunId: trace?.run_id || event.specialist_run_id || null,
        trace,
      });
    };

    client.on(EventType.CLARIFY, handleClarify);
    return () => { client.off(EventType.CLARIFY, handleClarify); };
  }, [client, addMessage, updateMessages, activeConversationRef, lastUserQueryRef]);

  // ── Submit clarification answers ──────────────────────────────────────────
  const submitClarification = useCallback((answers) => {
    if (!pendingClarification) return;

    let parsedAnswers = {};
    try {
      parsedAnswers = JSON.parse(answers);
    } catch {
      parsedAnswers = { raw: answers };
    }

    const context = buildClarificationContext({
      parentQueryId: pendingClarification.queryId,
      specialistRunId: pendingClarification.specialistRunId,
      originalQuery: pendingClarification.originalQuery,
      questions: pendingClarification.questions || [],
      answers: parsedAnswers,
      metadata: {
        agent_id: pendingClarification.agentId,
        trace: pendingClarification.trace || undefined,
      },
    });
    client.sendClarifyResponse(pendingClarification.queryId, JSON.stringify(context));

    const ts = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const convId = activeConversationRef.current;

    const displayText = formatAnswers(answers, pendingClarification.questions || []);

    addMessage(convId, {
      id: generateMsgId(),
      role: 'user',
      content: displayText,
      timestamp: ts,
    });
    addMessage(convId, {
      id: generateMsgId(),
      role: 'orchestrator',
      content: 'Got it! Processing your response...',
      timestamp: ts,
      agentId: 'orchestrator',
      delegations: [],
      isStreaming: true,
    });

    setPendingClarification(null);
  }, [pendingClarification, client, addMessage, activeConversationRef]);

  const contextValue = useMemo(() => ({
    pendingClarification,
    submitClarification,
  }), [pendingClarification, submitClarification]);

  return (
    <ClarificationContext.Provider value={contextValue}>
      {children}
    </ClarificationContext.Provider>
  );
}

export function useClarification() {
  const ctx = useContext(ClarificationContext);
  if (!ctx) throw new Error('useClarification must be used within ClarificationProvider');
  return ctx;
}
